import { IconInfo } from "@/assets/icons/geist/IconInfo";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { ExternalLink } from "@/components/podkit/typography/Link";
import { Text } from "@/components/podkit/typography/Text";
import type { PlainSecret } from "@/queries/secret-queries";
import { useCallback, useEffect, useId, useMemo, useState, type FC } from "react";

type RegistryCredentials = {
    username: string;
    password: string;
};

export type EditSecretFormOnSubmitInput = {
    secretValuePlaintext: string;
};

export const EditSecretForm: FC<{
    secret: PlainSecret;
    formName: string;
    onDisabledChange: (disabled: boolean) => void;
    onSubmit: (values: EditSecretFormOnSubmitInput) => Promise<void>;
}> = ({ secret, formName, onSubmit, onDisabledChange }) => {
    const nameId = useId();
    const secretId = useId();
    const registryHostId = useId();
    const registryUsernameId = useId();
    const registryPasswordId = useId();

    const [newSecretValue, setNewSecretValue] = useState<string>("");
    const [registryCredentials, setRegistryCredentials] = useState<RegistryCredentials>({
        username: "",
        password: "",
    });

    const isEcrRegistry = useMemo(() => {
        // Check if it's a container registry secret
        if (secret.mount?.case !== "containerRegistryBasicAuthHost") return false;

        const host = secret.mount.value;
        return host.includes(".dkr.ecr.") && host.includes(".amazonaws.com");
    }, [secret.mount]);

    // Create a type which narrows down the type of the mount
    type ContainerRegistrySecret = PlainSecret & {
        mount: Extract<PlainSecret["mount"], { case: "containerRegistryBasicAuthHost" }>;
    };

    // This uses the typescript "type predicates" feature to help typescript narrow the type of secret in a type-safe way - e.g. if you have a typo in that string "containerRegistryBasicAuthHost" then it won't compile.
    function isRegistrySecretPredicate(secret: PlainSecret): secret is ContainerRegistrySecret {
        return secret.mount?.case === "containerRegistryBasicAuthHost";
    }

    const isRegistrySecret = isRegistrySecretPredicate(secret);

    const handleSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (isRegistrySecret) {
                const authStr = `${registryCredentials.username}:${registryCredentials.password}`;
                await onSubmit({ secretValuePlaintext: btoa(authStr) });
            } else {
                await onSubmit({ secretValuePlaintext: newSecretValue });
            }
        },
        [onSubmit, newSecretValue, registryCredentials, isRegistrySecret],
    );

    useEffect(() => {
        const isValid = () => {
            if (isRegistrySecret) {
                return registryCredentials.username && registryCredentials.password;
            }
            return newSecretValue !== "";
        };

        onDisabledChange(!isValid());
    }, [newSecretValue, registryCredentials, onDisabledChange, isRegistrySecret]);

    const renderSecretInput = () => {
        if (isRegistrySecret) {
            return (
                <div className="flex flex-col gap-4">
                    <InputField id={registryHostId} label="Registry hostname">
                        <Input
                            id={registryHostId}
                            type="text"
                            name="registry-hostname"
                            value={secret.mount?.value}
                            disabled={true}
                            data-testid="registry-hostname-input"
                        />
                    </InputField>

                    {isEcrRegistry && (
                        <div className="flex flex-row gap-3 rounded-xl border border-border-base bg-surface-glass px-3 py-4">
                            <IconInfo size="base" className="text-blue-500" />
                            <div className="flex w-full grow flex-col">
                                <p className="text-base">
                                    ECR registry detected. Credentials set to use IAM authentication and cannot be
                                    modified.
                                </p>
                                <p className="text-sm">
                                    <ExternalLink href="https://www.gitpod.io/docs/flex/configuration/secrets/container-registry-secret#using-aws-ecr-with-iam-authentication">
                                        Learn more about ECR authentication
                                    </ExternalLink>
                                </p>
                            </div>
                        </div>
                    )}
                    <InputField id={registryUsernameId} label="Registry username">
                        <Input
                            id={registryUsernameId}
                            type="text"
                            name="registry-username"
                            value={registryCredentials.username}
                            placeholder="Enter registry username"
                            onChange={(e) => setRegistryCredentials((prev) => ({ ...prev, username: e.target.value }))}
                            autoComplete="off"
                            data-testid="registry-username-input"
                            disabled={isEcrRegistry}
                        />
                    </InputField>
                    <InputField id={registryPasswordId} label="Registry password">
                        <Input
                            id={registryPasswordId}
                            type="password"
                            name="registry-password"
                            value={registryCredentials.password}
                            placeholder="Enter registry password"
                            onChange={(e) => setRegistryCredentials((prev) => ({ ...prev, password: e.target.value }))}
                            autoComplete="off"
                            data-testid="registry-password-input"
                            disabled={isEcrRegistry}
                        />
                    </InputField>
                </div>
            );
        }

        return (
            <InputField id={secretId} label="Secret">
                <Textarea
                    id={secretId}
                    name="secret-value"
                    rows={10}
                    value={newSecretValue}
                    onChange={(e) => setNewSecretValue(e.target.value)}
                    className="resize-none"
                    data-testid="secret-value-input"
                />
            </InputField>
        );
    };

    const secretMountText = useMemo(() => {
        switch (secret.mount.case) {
            case "environmentVariable":
                return "Environment variable";
            case "containerRegistryBasicAuthHost":
                return `Container registry host: ${secret.mount.value}`;
            case "filePath":
                return `File: ${secret.mount.value}`;
            default:
                return "";
        }
    }, [secret.mount]);

    return (
        <form
            onSubmit={handleSubmit}
            name={formName}
            id={formName}
            data-testid="edit-secret-form"
            className="flex flex-col gap-4"
        >
            <InputField label="Name" id={nameId} disabled={true}>
                <Input
                    id={nameId}
                    type="text"
                    name="secret-name"
                    value={secret.name}
                    disabled={true}
                    data-testid="secret-name-input"
                />
            </InputField>

            {isRegistrySecret ? (
                renderSecretInput()
            ) : (
                <div className="flex flex-col gap-1">
                    {renderSecretInput()}
                    {secretMountText && <Text className="text-sm text-content-tertiary">{secretMountText}</Text>}
                </div>
            )}
        </form>
    );
};
